import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Save, Upload, FileCheck2, Trash2, Receipt, Star, Mail, Lock, Eye, RefreshCw, MessageSquare } from "lucide-react";
import { getUrgency } from "./urgency";
import EmailLocatoreModal from "./EmailLocatoreModal";
import api, { API, formatApiError, formatEuro } from "../../lib/api";
import AdminShell from "./AdminShell";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { EVALUATION_QUESTIONS } from "../../context/FormContext";

const STATUSES = [
  "bozza", "inviata", "in_verifica", "documenti_mancanti",
  "contratto_approvato", "approvata", "attestazione_inviata", "respinta",
];

const STATUS_FRIENDLY = {
  bozza: "Bozza",
  inviata: "Inviata",
  in_verifica: "In verifica",
  documenti_mancanti: "Documenti mancanti",
  contratto_approvato: "Contratto approvato — firma abilitata",
  approvata: "Approvata",
  attestazione_inviata: "Attestazione inviata",
  respinta: "Respinta",
};

function KV({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-2 border-b border-slate-100 last:border-0">
      <div className="text-xs text-slate-500 uppercase tracking-wider w-full sm:w-52 shrink-0">{label}</div>
      <div className="text-sm text-slate-900">{children ?? "—"}</div>
    </div>
  );
}

export default function SubmissionDetail() {
  const { id } = useParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [uploadingFinal, setUploadingFinal] = useState(false);
  const finalInputRef = useRef(null);
  const [lockInfo, setLockInfo] = useState(null); // null=checking, {isMine, blockedBy?, ...}
  const [lockChecking, setLockChecking] = useState(true);
  const [previewFile, setPreviewFile] = useState(null); // {id, filename, url}
  const [replaceBusy, setReplaceBusy] = useState(null); // file_id
  const [changeReqOpen, setChangeReqOpen] = useState(false);
  const [changeReqNote, setChangeReqNote] = useState("");
  const [changeReqDocs, setChangeReqDocs] = useState([]);
  const [changeReqSteps, setChangeReqSteps] = useState([]);
  const [changeReqBusy, setChangeReqBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/submissions/${id}`);
      setSub(r.data);
      setStatus(r.data.status);
      setNotes(r.data.admin_notes || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Concurrent-editing lock lifecycle
  useEffect(() => {
    let mounted = true;
    const acquire = async () => {
      try {
        const r = await api.post(`/admin/submissions/${id}/lock/acquire`);
        if (!mounted) return;
        setLockInfo({ isMine: true, ...r.data });
      } catch (e) {
        if (!mounted) return;
        if (e?.response?.status === 409) {
          const d = e.response.data?.detail || {};
          setLockInfo({
            isMine: false,
            blockedBy: d.locked_by || "",
            since: d.locked_since || "",
            expires: d.expires_at || "",
            message: d.message || "",
          });
        } else {
          toast.error(formatApiError(e));
        }
      } finally {
        if (mounted) setLockChecking(false);
      }
    };
    acquire();
    const heartbeatId = setInterval(() => {
      api.post(`/admin/submissions/${id}/lock/acquire`).catch(() => {});
    }, 60000);
    const onUnload = () => {
      const token = localStorage.getItem("cc_admin_token");
      if (!token) return;
      const url = `${api.defaults.baseURL}/admin/submissions/${id}/lock/release`;
      fetch(url, {
        method: "POST",
        keepalive: true,
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      mounted = false;
      clearInterval(heartbeatId);
      window.removeEventListener("beforeunload", onUnload);
      api.post(`/admin/submissions/${id}/lock/release`).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const forceReleaseLock = async () => {
    try {
      await api.post(`/admin/submissions/${id}/lock/force-release`);
      toast.success("Blocco rimosso. Riprovo ad accedere…");
      // Re-attempt acquire
      setLockChecking(true);
      const r = await api.post(`/admin/submissions/${id}/lock/acquire`);
      setLockInfo({ isMine: true, ...r.data });
      setLockChecking(false);
    } catch (e) {
      toast.error(formatApiError(e));
      setLockChecking(false);
    }
  };

  const saveStatus = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/submissions/${id}`, { status, admin_notes: notes });
      toast.success("Richiesta aggiornata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const [togglingAde, setTogglingAde] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const toggleAdeFatturata = async (nextValue) => {
    setTogglingAde(true);
    try {
      await api.patch(`/admin/submissions/${id}/ade-fatturata`, { ade_fatturata: nextValue });
      toast.success(nextValue ? "AdE marcata come registrata" : "Contrassegno di registrazione rimosso");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTogglingAde(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("cc_admin_token");
      const res = await fetch(`${API}/admin/submissions/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        toast.error("Sessione scaduta, effettua nuovamente il login");
        localStorage.removeItem("cc_admin_token");
        window.location.href = "/admin/login";
        return;
      }
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
      }
      const blob = await res.blob();
      if (blob.size === 0) throw new Error("PDF vuoto");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `attestazione_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download PDF error:", e);
      toast.error(e.message || "Errore nel download del PDF");
    }
  };

  const downloadFile = async (file) => {
    try {
      const token = localStorage.getItem("cc_admin_token");
      const res = await fetch(`${API}/admin/submissions/${id}/files/${file.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        toast.error("Sessione scaduta, effettua nuovamente il login");
        localStorage.removeItem("cc_admin_token");
        window.location.href = "/admin/login";
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = file.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download file error:", e);
      toast.error(e.message || "Errore download file");
    }
  };

  const previewFileHandler = async (file) => {
    try {
      const token = localStorage.getItem("cc_admin_token");
      const res = await fetch(`${API}/admin/submissions/${id}/files/${file.id}?inline=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewFile({ id: file.id, filename: file.filename, url });
    } catch (e) {
      toast.error(e.message || "Anteprima non disponibile");
    }
  };

  const replaceFile = async (file, newFile) => {
    if (!newFile) return;
    if (newFile.type !== "application/pdf") {
      toast.error("Solo PDF consentito");
      return;
    }
    setReplaceBusy(file.id);
    try {
      const token = localStorage.getItem("cc_admin_token");
      const fd = new FormData();
      fd.append("file", newFile);
      const res = await fetch(`${API}/admin/submissions/${id}/files/${file.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      toast.success(`"${file.filename}" sostituito con "${newFile.name}"`);
      await load();
    } catch (e) {
      toast.error(e.message || "Sostituzione fallita");
    } finally {
      setReplaceBusy(null);
    }
  };

  const submitChangeRequest = async () => {
    const note = changeReqNote.trim();
    if (note.length < 3) { toast.error("Scrivi una nota descrittiva (min 3 caratteri)"); return; }
    setChangeReqBusy(true);
    try {
      await api.post(`/admin/submissions/${id}/request-changes`, {
        note,
        docs_to_reupload: changeReqDocs,
        steps_to_edit: changeReqSteps,
      });
      toast.success("Richiesta di modifiche inviata al locatore");
      setChangeReqOpen(false);
      setChangeReqNote("");
      setChangeReqDocs([]);
      setChangeReqSteps([]);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setChangeReqBusy(false); }
  };

  const uploadFinalAttestation = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Solo PDF consentito");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File troppo grande (max 15 MB)");
      return;
    }
    setUploadingFinal(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/submissions/${id}/attestazione`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attestazione firmata caricata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingFinal(false);
      if (finalInputRef.current) finalInputRef.current.value = "";
    }
  };

  const downloadFinalAttestation = async () => {
    try {
      const token = localStorage.getItem("cc_admin_token");
      const res = await fetch(`${API}/admin/submissions/${id}/attestazione`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        toast.error("Sessione scaduta, effettua nuovamente il login");
        localStorage.removeItem("cc_admin_token");
        window.location.href = "/admin/login";
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = sub?.final_attestation?.filename || "attestazione_firmata.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download attestation error:", e);
      toast.error(e.message || "Errore download attestazione");
    }
  };

  const deleteFinalAttestation = async () => {
    if (!window.confirm("Eliminare l'attestazione firmata caricata?")) return;
    try {
      await api.delete(`/admin/submissions/${id}/attestazione`);
      toast.success("Attestazione firmata rimossa");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AdminShell
      title={
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-slate-400 hover:text-slate-900"><ArrowLeft size={18} /></Link>
          Dettaglio Richiesta
        </div>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setChangeReqOpen(true)}
            variant="outline"
            size="sm"
            disabled={loading || !sub || !lockInfo?.isMine}
            className="border-orange-300 text-orange-800 hover:bg-orange-50"
            data-testid="open-change-request"
          >
            <MessageSquare size={14} className="mr-2" /> Richiedi modifiche
          </Button>
          <Button
            onClick={() => setEmailModalOpen(true)}
            variant="outline"
            size="sm"
            disabled={loading || !sub || !lockInfo?.isMine}
            data-testid="open-email-locatore"
          >
            <Mail size={14} className="mr-2" /> Invia email al Locatore
          </Button>
          <Button onClick={downloadPdf} variant="outline" size="sm" data-testid="download-pdf">
            <Download size={14} className="mr-2" /> PDF
          </Button>
        </div>
      }
    >
      {lockChecking && (
        <div className="text-slate-500 text-sm" data-testid="lock-checking">Verifica accesso in corso…</div>
      )}
      {!lockChecking && lockInfo && !lockInfo.isMine && (
        <div className="max-w-2xl mx-auto mt-6 rounded-lg border-2 border-red-200 bg-red-50 p-8" data-testid="lock-blocked-screen">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Lock size={24} className="text-red-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-serif text-2xl text-slate-900 mb-2">Pratica in uso</h2>
              <p className="text-slate-700 mb-2" data-testid="lock-blocked-message">
                Non puoi accedere a questa pratica: attualmente ci sta lavorando l&apos;utente{" "}
                <strong className="text-red-800" data-testid="lock-blocked-by">{lockInfo.blockedBy || "sconosciuto"}</strong>.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Il blocco scade automaticamente dopo qualche minuto di inattività.
                Riprova più tardi oppure — se sai che il collega non sta effettivamente lavorando alla pratica —
                puoi forzare la rimozione del blocco.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link to="/admin">
                  <Button variant="outline" size="sm" data-testid="lock-back-to-list">
                    <ArrowLeft size={14} className="mr-1.5" /> Torna alla lista
                  </Button>
                </Link>
                <Button
                  onClick={forceReleaseLock}
                  variant="outline"
                  size="sm"
                  className="border-red-200 text-red-800 hover:bg-red-100"
                  data-testid="lock-force-release"
                >
                  Forza sblocco
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {!lockChecking && lockInfo?.isMine && loading && <div className="text-slate-500">Caricamento...</div>}
      {!lockChecking && lockInfo?.isMine && !loading && sub && (
        <div className="space-y-6">
          {sub.is_priority && (
            <div
              className="flex items-center gap-3 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 px-5 py-4 shadow-md ring-1 ring-amber-600/20"
              data-testid="detail-priority-ribbon"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/40 flex items-center justify-center">
                <Star size={20} className="fill-amber-700 text-amber-800" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-widest font-semibold text-amber-900">
                  Cliente Priority
                </p>
                <p className="text-sm font-bold">
                  Associato riconosciuto: <span data-testid="detail-priority-matched">{sub.priority_matched_name || "—"}</span>
                </p>
                <p className="text-xs text-amber-900/80">Gestisci questa pratica con priorità.</p>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {sub.data?.richiedente?.tipo && (
              <Card title="Richiedente della pratica">
                <KV label="Tipo">
                  {sub.data.richiedente.tipo === "agenzia" ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full px-2 py-0.5"
                      data-testid="detail-richiedente-tipo"
                    >
                      👤 Professionista incaricato
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5"
                      data-testid="detail-richiedente-tipo"
                    >
                      👤 Locatore (compila direttamente)
                    </span>
                  )}
                </KV>
                {sub.data.richiedente.tipo === "agenzia" && (
                  <>
                    <KV label="Email Professionista incaricato">
                      <span data-testid="detail-richiedente-email">{sub.data.richiedente.email || "—"}</span>
                    </KV>
                    <KV label="Nome Professionista incaricato / Studio">
                      <span data-testid="detail-richiedente-agenzia-nome">{sub.data.richiedente.agenzia_nome || "—"}</span>
                    </KV>
                    <KV label="Nome referente">
                      <span data-testid="detail-richiedente-agenzia-referente">{sub.data.richiedente.agenzia_referente || "—"}</span>
                    </KV>
                  </>
                )}
              </Card>
            )}

            <Card title={
              (sub.data?.locatori && sub.data.locatori.length > 1)
                ? `Dati Locatori/Locatrici (${sub.data.locatori.length})`
                : "Dati Locatore/Locatrice"
            }>
              {(() => {
                const list = (sub.data?.locatori && sub.data.locatori.length)
                  ? sub.data.locatori
                  : [{ nome: sub.data?.locatore?.nome, email: sub.data?.locatore?.email }];
                return list.map((p, i) => (
                  <div key={i} data-testid={`detail-locatore-${i}`} className={i > 0 ? "mt-2 pt-2 border-t border-slate-100" : ""}>
                    {list.length > 1 && (
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold px-5 pt-2">
                        Locatore n. {i + 1}
                      </p>
                    )}
                    <KV label="Nome e Cognome">{p.nome || "—"}</KV>
                    <KV label="Email">{p.email || "—"}</KV>
                  </div>
                ));
              })()}
            </Card>

            <Card title={
              (sub.data?.conduttori && sub.data.conduttori.length > 1)
                ? `Dati Conduttori/Conduttrici (${sub.data.conduttori.length})`
                : "Dati Conduttore/Conduttrice"
            }>
              {(() => {
                const list = (sub.data?.conduttori && sub.data.conduttori.length)
                  ? sub.data.conduttori
                  : [{ nome: sub.data?.conduttore?.nome }];
                return list.map((p, i) => (
                  <div key={i} data-testid={`detail-conduttore-${i}`} className={i > 0 ? "mt-2 pt-2 border-t border-slate-100" : ""}>
                    {list.length > 1 && (
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold px-5 pt-2">
                        Conduttore n. {i + 1}
                      </p>
                    )}
                    <KV label="Nome e Cognome">{p.nome || "—"}</KV>
                  </div>
                ));
              })()}
            </Card>

            <Card title="Immobile">
              <KV label="Zona">Zona {sub.data?.zona}</KV>
              <KV label="Via / Piazza">{sub.data?.immobile?.via}</KV>
              <KV label="Civico">{sub.data?.immobile?.civico}</KV>
              <KV label="Piano">{sub.data?.immobile?.piano}</KV>
              <KV label="CAP">{sub.data?.immobile?.cap}</KV>
              <KV label="Città">{sub.data?.immobile?.citta}</KV>
              <KV label="Provincia">{sub.data?.immobile?.provincia}</KV>
            </Card>

            <Card title="Estremi Catastali">
              <KV label="Foglio">{sub.data?.catasto?.foglio}</KV>
              <KV label="Particella">{sub.data?.catasto?.particella}</KV>
              <KV label="Subalterno">{sub.data?.catasto?.subalterno}</KV>
              {(sub.data?.catasto?.pertinenze || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Pertinenze</div>
                  <ul className="space-y-1.5 text-sm text-slate-700">
                    {(sub.data?.catasto?.pertinenze || []).map((p, i) => {
                      const labels = { cantina: "Cantina", box: "Box/Autorimessa", posto_auto: "Posto auto", soffitta: "Soffitta", balcone: "Balcone", terrazzo: "Terrazzo", superficie_scoperta: "Superficie scoperta / Giardino" };
                      return (
                        <li key={i}>
                          <strong>{labels[p.tipo] || p.tipo || "—"}</strong>{" "}
                          <span className="text-slate-500">— Foglio {p.foglio || "—"}, Part. {p.particella || "—"}, Sub {p.subalterno || "—"}{p.mq ? `, ${p.mq} mq` : ""}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </Card>

            <Card title="Contratto">
              <KV label="Tipologia">{sub.data?.contratto?.tipologia}</KV>
              <KV label="Dal">{sub.data?.contratto?.data_inizio}</KV>
              <KV label="Al">{sub.data?.contratto?.data_fine}</KV>
            </Card>

            <Card title="Valutazione">
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {EVALUATION_QUESTIONS.map((q, i) => {
                  const v = sub.data?.valutazione?.[`q${i + 1}`] || "no";
                  return (
                    <div key={i} className="flex items-center gap-3 py-1.5 border-b border-slate-100 text-xs">
                      <span className="text-slate-400 font-mono w-5">{i + 1}</span>
                      <span className="text-slate-700 flex-1 truncate">{q}</span>
                      <span className={`font-medium text-[10px] px-2 py-0.5 rounded ${v === "si" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>
                        {v === "si" ? "SÌ" : "NO"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card title="Attestazione Firmata">
              {sub.final_attestation ? (
                <div className="flex items-center justify-between gap-3 border border-green-800/30 bg-green-50 rounded-md px-4 py-3" data-testid="final-attestation-present">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileCheck2 size={20} className="text-green-800 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {sub.final_attestation.filename}
                      </div>
                      <div className="text-xs text-slate-500">
                        {(sub.final_attestation.size / 1024).toFixed(1)} KB · caricata il{" "}
                        {new Date(sub.final_attestation.uploaded_at).toLocaleString("it-IT")}
                        {sub.final_attestation.uploaded_by && ` da ${sub.final_attestation.uploaded_by}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={downloadFinalAttestation}
                      data-testid="download-final"
                    >
                      <Download size={14} className="mr-1.5" /> Scarica
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={deleteFinalAttestation}
                      className="text-slate-500 hover:text-red-700"
                      data-testid="delete-final"
                      aria-label="Rimuovi attestazione firmata"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                    Una volta firmata dalle Organizzazioni, carica qui il PDF
                    dell&apos;Attestazione definitiva. Sarà disponibile per il download da questa pagina.
                  </p>
                  <label
                    className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer transition-colors px-6 py-6 hover:bg-slate-50 ${uploadingFinal ? "opacity-60 pointer-events-none" : ""}`}
                    data-testid="final-attestation-upload"
                  >
                    <Upload size={20} className="text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">
                      {uploadingFinal ? "Caricamento..." : "Clicca o trascina il PDF firmato"}
                    </span>
                    <span className="text-xs text-slate-500">Solo PDF · max 15 MB</span>
                    <input
                      ref={finalInputRef}
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => uploadFinalAttestation(e.target.files?.[0])}
                      data-testid="final-attestation-input"
                    />
                  </label>
                </div>
              )}
            </Card>

            <Card title="Documenti Allegati">
              {sub.admin_change_request && (
                <div className="mb-4 border border-orange-300 bg-orange-50 rounded-md p-3" data-testid="active-change-request-banner">
                  <p className="text-sm font-semibold text-orange-900">Richiesta di modifiche attiva</p>
                  <p className="text-xs text-orange-800 mt-1 whitespace-pre-wrap">{sub.admin_change_request.note}</p>
                  {(sub.admin_change_request.docs_to_reupload || []).length > 0 && (
                    <p className="text-[11px] text-orange-800 mt-2">
                      <strong>Documenti da ricaricare:</strong> {sub.admin_change_request.docs_to_reupload.join(", ")}
                    </p>
                  )}
                  <p className="text-[10px] text-orange-700 mt-2">
                    Inviata da {sub.admin_change_request.created_by} il {new Date(sub.admin_change_request.created_at).toLocaleString("it-IT")}
                  </p>
                </div>
              )}
              {(sub.files || []).length === 0 ? (
                <div className="text-sm text-slate-500">Nessun documento caricato</div>
              ) : (
                <ul className="space-y-2">
                  {sub.files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-2" data-testid={`file-${f.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText size={16} className="text-slate-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{f.filename}</div>
                          <div className="text-xs text-slate-500">
                            {f.doc_type} · {(f.size / 1024).toFixed(1)} KB
                            {f.replaced_at && <span className="ml-1 text-orange-700">· sostituito da {f.replaced_by}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => previewFileHandler(f)} data-testid={`preview-file-${f.id}`} title="Anteprima">
                          <Eye size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadFile(f)} data-testid={`download-file-${f.id}`} title="Scarica">
                          <Download size={14} />
                        </Button>
                        <label className="inline-flex items-center gap-1 px-2 h-8 text-xs text-slate-600 hover:bg-slate-100 rounded cursor-pointer" title="Sostituisci con una nuova versione" data-testid={`replace-file-${f.id}`}>
                          {replaceBusy === f.id ? "…" : <RefreshCw size={14} />}
                          <input
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => replaceFile(f, e.target.files?.[0])}
                            disabled={replaceBusy === f.id}
                          />
                        </label>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <aside className="space-y-6">
            <Card title="Risultato Calcolo">
              {sub.calculation ? (
                <>
                  <KV label="Zona">Zona {sub.calculation.zona}</KV>
                  <KV label="Punteggio">{sub.calculation.punteggio}/47</KV>
                  <KV label="Sub-fascia">{sub.calculation.sub_fascia}</KV>
                  <KV label="MQ riparametrati">{sub.calculation.mq_riparametrati}</KV>
                  <KV label="Variazione %">{sub.calculation.variazione_percentuale}%</KV>
                  <KV label="Canone min mensile">
                    <strong>{formatEuro(sub.calculation.canone_minimo_mensile)}</strong>
                  </KV>
                  <KV label="Canone max mensile">
                    <strong>{formatEuro(sub.calculation.canone_massimo_mensile)}</strong>
                  </KV>
                  <KV label="Canone convenuto">
                    {formatEuro(sub.data?.step9?.canone_convenuto || 0)}
                  </KV>
                </>
              ) : (
                <div className="text-sm text-slate-500">Nessun calcolo disponibile</div>
              )}
            </Card>

            <Card title="Gestione Pratica">
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Stato</label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_FRIENDLY[s] || s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Note interne</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder="Note visibili solo all'amministratore"
                    data-testid="admin-notes"
                  />
                </div>
                <Button onClick={saveStatus} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-status">
                  <Save size={14} className="mr-2" />
                  {saving ? "Salvataggio..." : "Salva modifiche"}
                </Button>
              </div>
            </Card>

            {sub?.data?.registrazione_ade === true && (
              <Card title="Servizio AdE € 25,00">
                <div className="space-y-3" data-testid="ade-invoicing-card">
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Il locatore ha richiesto la registrazione del contratto presso l&apos;Agenzia
                    delle Entrate come servizio a pagamento.
                  </p>
                  {sub?.ade_fatturata ? (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                      <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                        <Receipt size={14} /> Fatturata
                      </p>
                      {sub?.ade_fatturata_at && (
                        <p className="text-[11px] text-emerald-800/80 mt-1">
                          Marcata il {new Date(sub.ade_fatturata_at).toLocaleString("it-IT")}
                          {sub?.ade_fatturata_by && <> da {sub.ade_fatturata_by}</>}
                        </p>
                      )}
                      <Button
                        onClick={() => toggleAdeFatturata(false)}
                        disabled={togglingAde}
                        variant="outline"
                        className="w-full mt-3 text-xs"
                        data-testid="ade-unmark-btn"
                      >
                        Rimuovi contrassegno registrata
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                      <p className="text-sm font-semibold text-amber-900">
                        Da registrare · € 25,00
                      </p>
                      <p className="text-[11px] text-amber-800/80 mt-1">
                        Emetti la fattura per la registrazione al locatore e poi marca la pratica come registrata.
                      </p>
                      <Button
                        onClick={() => toggleAdeFatturata(true)}
                        disabled={togglingAde}
                        className="w-full mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="ade-mark-btn"
                      >
                        <Receipt size={14} className="mr-2" />
                        {togglingAde ? "Salvataggio…" : "Marca AdE come registrata"}
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            )}

            <Card title="Metadati">
              <KV label="ID">{sub.id}</KV>
              <KV label="Creata">{new Date(sub.created_at).toLocaleString("it-IT")}</KV>
              <KV label="Aggiornata">{new Date(sub.updated_at).toLocaleString("it-IT")}</KV>
              <KV label="Inviata">{sub.submitted_at ? new Date(sub.submitted_at).toLocaleString("it-IT") : "—"}</KV>
              {(() => {
                const u = getUrgency(sub.submitted_at, sub.status);
                return u ? (
                  <KV label="Urgenza">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${u.pillClass}`}
                      title={u.tooltip}
                      data-testid="detail-urgency-badge"
                    >
                      <span className={`w-2 h-2 rounded-full ${u.dotClass}`} />
                      {u.label}
                    </span>
                  </KV>
                ) : null;
              })()}
            </Card>

            {sub.admin_emails_sent && sub.admin_emails_sent.length > 0 && (
              <Card title={`Email inviate al locatore (${sub.admin_emails_sent.length})`}>
                <div className="p-5 space-y-3" data-testid="admin-emails-history">
                  {[...sub.admin_emails_sent].reverse().map((m) => (
                    <div key={m.id} className="border border-slate-200 rounded-md p-3 bg-slate-50">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-sm font-semibold text-slate-900 truncate" title={m.subject}>
                          {m.subject}
                        </p>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 whitespace-nowrap">
                          {m.sent_at ? new Date(m.sent_at).toLocaleString("it-IT") : ""}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mb-2">
                        → {m.to} · da {m.sent_by || "admin"}
                      </p>
                      <p className="text-xs text-slate-700 whitespace-pre-wrap line-clamp-4">
                        {m.body}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </aside>
        </div>
        </div>
      )}
      <EmailLocatoreModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        submission={sub}
        onSent={() => load()}
      />

      {/* Preview PDF modal */}
      {previewFile && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(previewFile.url); setPreviewFile(null); }} data-testid="preview-modal">
          <div className="bg-white rounded-lg overflow-hidden shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
              <p className="text-sm font-medium text-slate-800 truncate">Anteprima · {previewFile.filename}</p>
              <button className="text-slate-500 hover:text-slate-900 text-2xl leading-none" onClick={() => { URL.revokeObjectURL(previewFile.url); setPreviewFile(null); }} data-testid="preview-close">×</button>
            </div>
            <iframe src={previewFile.url} title={previewFile.filename} className="flex-1 w-full border-0" data-testid="preview-iframe" />
          </div>
        </div>
      )}

      {/* Richiedi modifiche modal */}
      {changeReqOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setChangeReqOpen(false)} data-testid="change-request-modal">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Punto 5.c</p>
            <h3 className="text-lg font-serif text-slate-900 mb-3">Richiedi modifiche al locatore</h3>
            <p className="text-xs text-slate-600 mb-4">
              Il locatore riceverà un banner arancione nel suo pannello e potrà ricaricare
              <strong> solo</strong> i documenti che seleziona qui. Il testo della nota verrà
              mostrato in evidenza per guidarlo nella correzione.
            </p>
            <label className="block text-xs font-medium text-slate-700 mb-1">Nota da mostrare al locatore *</label>
            <Textarea
              value={changeReqNote}
              onChange={(e) => setChangeReqNote(e.target.value)}
              rows={4}
              placeholder="Es. La visura catastale è del 2019, ricarica quella aggiornata degli ultimi 6 mesi. Correggi anche il codice fiscale del conduttore (attualmente non valido)."
              data-testid="change-req-note"
            />
            <p className="text-xs font-medium text-slate-700 mt-4 mb-1">Documenti da ricaricare (opzionale)</p>
            <p className="text-[11px] text-slate-500 mb-2">Se ne selezioni almeno uno, il locatore potrà caricare <strong>solo</strong> questi documenti. Gli altri restano bloccati.</p>
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded p-2 space-y-1">
              {[
                ["visura_catastale","Visura Catastale"],
                ["planimetria_catastale","Planimetria Catastale"],
                ["ape","APE"],
                ["contratto_locazione","Contratto"],
                ["scheda_iscrizione","Scheda d'iscrizione"],
                ["documento_cf_locatore","Doc. CF Locatore"],
                ["documento_cf_conduttore","Doc. CF Conduttore"],
                ["ricevuta_bonifico","Ricevuta bonifico"],
                ["doc_transitorieta","Doc. Transitorietà"],
                ["doc_studenti","Iscrizione universitaria"],
              ]
                // Iscrizione universitaria non è pertinente per il contratto Transitorio (non-universitario)
                .filter(([k]) => !(k === "doc_studenti" && (sub?.data?.contratto?.tipologia || "").toLowerCase() === "transitorio"))
                .map(([k,l]) => (
                <label key={k} className="flex items-center gap-2 text-xs text-slate-800 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                  <input
                    type="checkbox"
                    checked={changeReqDocs.includes(k)}
                    onChange={(e) => setChangeReqDocs((prev) => e.target.checked ? [...prev, k] : prev.filter(x => x !== k))}
                    data-testid={`change-req-doc-${k}`}
                  />
                  {l}
                </label>
              ))}
            </div>

            <p className="text-xs font-medium text-slate-700 mt-4 mb-1">Sezioni dati del form da riaprire (opzionale)</p>
            <p className="text-[11px] text-slate-500 mb-2">Se selezioni almeno uno step, il locatore potrà riaprire il modulo e modificare <strong>solo</strong> quelle sezioni. Gli altri step restano bloccati con icona 🔒.</p>
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded p-2 grid grid-cols-2 gap-x-2">
              {[
                [2, "Step 2 — Locatore"],
                [3, "Step 3 — Conduttore"],
                [4, "Step 4 — Immobile / Zona"],
                [5, "Step 5 — Dati Catastali"],
                [6, "Step 6 — Contratto"],
                [7, "Step 7 — Valutazione"],
                [8, "Step 8 — Superfici"],
                [9, "Step 9 — Maggiorazioni"],
                [12, "Step 12 — Dichiarazione / AdE"],
              ].map(([k,l]) => (
                <label key={k} className="flex items-center gap-2 text-xs text-slate-800 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                  <input
                    type="checkbox"
                    checked={changeReqSteps.includes(k)}
                    onChange={(e) => setChangeReqSteps((prev) => e.target.checked ? [...prev, k] : prev.filter(x => x !== k))}
                    data-testid={`change-req-step-${k}`}
                  />
                  {l}
                </label>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setChangeReqOpen(false)} data-testid="change-req-cancel">Annulla</Button>
              <Button onClick={submitChangeRequest} disabled={changeReqBusy} className="bg-orange-700 hover:bg-orange-800 text-white" data-testid="change-req-submit">
                {changeReqBusy ? "Invio…" : "Richiedi modifiche →"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg">
      <div className="px-5 py-3 border-b border-slate-100">
        <h3 className="font-serif text-lg text-slate-900">{title}</h3>
      </div>
      <div className="px-5 py-3">{children}</div>
    </div>
  );
}
